import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  User as UserIcon,
  ArrowLeft,
  TrendingUp,
  Building2,
  Calendar,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Heart,
  Lock,
  DollarSign,
  Award,
  Clock,
  Target,
  CheckCircle2,
  Users,
  Star,
  Layers,
  Globe,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useFranchise } from "@/hooks/useFranchise";
import { useOfferFranchise, useMyFranchiseOffers } from "@/hooks/useOffer";
import { useMySignedNdas } from "@/hooks/useNda";
import { useAuthStore } from "@/store/authStore";
import { can } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { useToggleSaveListing } from "@/hooks/useSavedListings";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/misc";
import { Spinner, NdaSignModal, OfferModal, MediaGallery } from "@/components/common";
import { cn } from "@/lib/utils";
import type { FranchiseImage } from "@/types";
import { formatNumber } from "@/utils";

/* ─── helpers ─── */
function formatPrice(val: string | number | null | undefined, currencyUnit: string = "VND") {
  if (!val && val !== 0) return "Liên hệ";
  const n = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(n)) return "Liên hệ";
  return formatNumber(n) + " " + currencyUnit;
}

function getImages(images: FranchiseImage[]) {
  if (!images?.length) return [];
  return images.map((i) => ({
    url: i.image?.publicUrl || (i as any).url || "",
    type: i.type,
    isPrimary: i.isPrimary,
  })).filter(img => Boolean(img.url));
}

const offerSchema = z.object({
  message: z.string().min(10, "Tối thiểu 10 ký tự"),
});
type OfferData = z.infer<typeof offerSchema>;

export default function FranchiseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: item, isLoading, error, refetch } = useFranchise(id!);
  const isNdaError = error && (error as any).response?.data?.message === "Please Sign NDA";
  const { mutate: createOffer, isPending } = useOfferFranchise();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = item ? isSaved(item.id) : false;
  const { data: myOffers } = useMyFranchiseOffers(user?.id);
  const isOffered = Array.isArray(myOffers) ? myOffers.some((o: any) => o.entityId === item?.id) : false;

  const { data: mySignedNdas } = useMySignedNdas();
  const isSignedNda = Array.isArray(mySignedNdas) ? mySignedNdas.some((n: any) => n.entityId === item?.id && n.entityType === 'Franchise') : false;
  const ndaRequired = (item as any)?.ndaRequired === true;
  const needsToSignNda = ndaRequired && !isSignedNda;

  const [imgIdx, setImgIdx] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OfferData>({
    resolver: zodResolver(offerSchema),
  });

  if (isLoading) return <Spinner />;

  if (isNdaError) {
    return (
      <div className="max-w-3xl mx-auto py-20 text-center animate-fade-in">
        <div className="bg-white rounded-2xl shadow-sm border p-10 max-w-lg mx-auto">
          <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">Yêu cầu bảo mật thông tin</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Tin đăng này yêu cầu bạn phải ký Thỏa thuận bảo mật thông tin (NDA) trước khi xem chi tiết và trao đổi với đối tác.
          </p>
          <Button
            onClick={() => setNdaOpen(true)}
            className="w-full btn-brand py-3 text-base mb-3"
          >
            Ký NDA để xem chi tiết
          </Button>
          <Link to="/nhuong-quyen">
            <Button variant="ghost" className="w-full">
              Quay lại danh sách
            </Button>
          </Link>
        </div>

        {/* ══ NDA MODAL (For Error Screen) ══ */}
        <NdaSignModal
          isOpen={ndaOpen}
          onClose={() => setNdaOpen(false)}
          entityType="Franchise"
          entityId={id!}
          onSuccess={() => {
            setNdaOpen(false);
            refetch();
          }}
        />
      </div>
    );
  }

  if (!item)
    return (
      <div className="text-center py-20">
        <p style={{ color: "var(--text-3)" }}>Không tìm thấy thương hiệu</p>
        <Link to="/nhuong-quyen">
          <Button variant="ghost" className="mt-4">
            Quay lại danh sách
          </Button>
        </Link>
      </div>
    );

  const imgs = getImages(item.images);
  const onSubmit = (data: OfferData) => {
    const payload = {
      entityType: "Franchise",
      entityId: item.id,
      receiverId: item.creator?.id || (item as any).createdBy || "",
      content: {
        title: item.name,
        terms: "Thỏa thuận trực tiếp",
      },
      message: data.message,
      status: "pending",
    };
    createOffer(payload as any, {
      onSuccess: () => {
        reset();
        setOfferOpen(false);
      },
    });
  };

  const fullAddr = [
    item.headquarters,
    item.district?.name,
    item.province?.name,
    item.country?.name,
  ]
    .filter(Boolean)
    .join(", ");

  const ownerId = item.creator?.id || (item as any).createdBy;

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between gap-4">
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--text-3)" }}
        >
          <Link
            to="/nhuong-quyen"
            className="flex items-center gap-1 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft size={14} /> Nhượng quyền
          </Link>
          <span>/</span>
          <span className="truncate max-w-xs" style={{ color: "var(--text-1)" }}>
            {item.name}
          </span>
        </div>

        {user?.id === ownerId && (
          <Link to={`/nhuong-quyen/sua/${item.id}`}>
            <Button variant="outline" size="sm" className="gap-2 font-bold shadow-sm h-9 border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700">
              Chỉnh sửa tin đăng
            </Button>
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── Left: Main content ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Image gallery */}
          <MediaGallery
            images={item.images}
            approvalStatus={item.approvalStatus}
            fallbackIcon="🏢"
          />

          {/* Title + Investment Block */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {(item.businessTypeName || (item as any).businessType) && (
                  <Badge variant="amber">{item.businessTypeName || (item as any).businessType}</Badge>
                )}
                {(item.businessSectorName || (item as any).businessSector) && (
                  <Badge variant="secondary">{item.businessSectorName || (item as any).businessSector}</Badge>
                )}
              </div>
              <h1
                className="font-display font-bold text-2xl mb-4"
                style={{ color: "var(--text-1)" }}
              >
                {item.name}
              </h1>

              {/* Price / Investment block */}
              <div
                className="rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x border-orange-200/50"
                style={{
                  background: "var(--brand-pale)",
                  border: "1px solid rgba(249,115,22,0.2)",
                }}
              >
                <div className="flex flex-col">
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Vốn đầu tư tối thiểu
                  </p>
                  <p
                    className="font-display font-black text-xl truncate"
                    style={{ color: "var(--brand)" }}
                    title={formatPrice(item.investment_required_min, item.currencyUnit)}
                  >
                    {formatPrice(item.investment_required_min, item.currencyUnit)}
                  </p>
                </div>
                <div className="flex flex-col md:pl-6 pt-4 md:pt-0 border-orange-200/50">
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Vốn đầu tư tối đa
                  </p>
                  <p
                    className="font-display font-bold text-lg truncate"
                    style={{ color: "var(--text-1)" }}
                    title={formatPrice(item.investment_required_max, item.currencyUnit)}
                  >
                    {formatPrice(item.investment_required_max, item.currencyUnit)}
                  </p>
                </div>
                <div className="flex flex-col md:pl-6 pt-4 md:pt-0 border-orange-200/50">
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Biên lợi nhuận
                  </p>
                  <p
                    className="font-display font-bold text-lg text-green-600 truncate"
                    title={item.profit_margin != null ? `~${item.profit_margin}%` : 'Đang cập nhật'}
                  >
                    {item.profit_margin != null ? `~${item.profit_margin}%` : 'Đang cập nhật'}
                  </p>
                </div>
              </div>

              {fullAddr && (
                <div
                  className="flex items-center gap-1.5 text-sm"
                  style={{ color: "var(--text-3)" }}
                >
                  <MapPin size={14} /> {fullAddr}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Giới thiệu thương hiệu</CardTitle>
            </CardHeader>
            <CardContent>
              <p
                className="text-sm leading-relaxed whitespace-pre-wrap"
                style={{ color: "var(--text-2)" }}
              >
                {item.description || "Chưa có thông tin mô tả chi tiết."}
              </p>
            </CardContent>
          </Card>

          {/* Investment & Financial Details */}
          <Card>
            <CardHeader>
              <CardTitle>Chi tiết đầu tư & Tài chính</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  {
                    icon: <DollarSign size={15} />,
                    label: "Vốn tối thiểu",
                    value: formatPrice(item.investment_required_min, item.currencyUnit),
                  },
                  {
                    icon: <DollarSign size={15} />,
                    label: "Vốn tối đa",
                    value: formatPrice(item.investment_required_max, item.currencyUnit),
                  },
                  {
                    icon: <Award size={15} />,
                    label: "Phí nhượng quyền",
                    value: formatPrice(item.franchise_fee, item.currencyUnit),
                  },
                  {
                    icon: <TrendingUp size={15} />,
                    label: "Phí bản quyền (Royalty)",
                    value: item.royalty_fee != null ? `${item.royalty_fee}%` : null,
                  },
                  {
                    icon: <TrendingUp size={15} />,
                    label: "Phí Marketing",
                    value: item.advertising_fee != null ? `${item.advertising_fee}%` : null,
                  },
                  {
                    icon: <Clock size={15} />,
                    label: "Thời hạn HĐ",
                    value: item.contract_duration != null ? `${item.contract_duration} năm` : null,
                  },
                  {
                    icon: <TrendingUp size={15} />,
                    label: "Doanh thu TB/Tháng",
                    value: formatPrice(item.average_revenue, item.currencyUnit),
                  },
                  {
                    icon: <TrendingUp size={15} />,
                    label: "Biên lợi nhuận",
                    value: item.profit_margin != null ? `~${item.profit_margin}%` : null,
                  },
                ].filter(i => i.value !== null && i.value !== "Liên hệ").map(({ icon, label, value }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{
                        background: "var(--brand-dim)",
                        color: "var(--brand)",
                      }}
                    >
                      {icon}
                    </div>
                    <div>
                      <p
                        className="text-[11px] font-medium"
                        style={{ color: "var(--text-3)" }}
                      >
                        {label}
                      </p>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "var(--text-1)" }}
                      >
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Requirements & Support */}
          <Card>
            <CardHeader>
              <CardTitle>Yêu cầu & Hỗ trợ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {item.requirements && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-3)" }}>
                    Yêu cầu đối tác
                  </p>
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-sm" style={{ color: "var(--text-2)" }}>
                      {item.requirements}
                    </p>
                  </div>
                </div>
              )}
              {item.support_provided && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--text-3)" }}>
                    Hỗ trợ từ thương hiệu
                  </p>
                  <div className="p-4 rounded-xl border border-orange-200/50" style={{ background: "var(--brand-pale)" }}>
                    <p className="text-sm" style={{ color: "var(--text-2)" }}>
                      {item.support_provided}
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                {item.training_program && (
                  <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                    <CheckCircle2 size={16} />
                    <span>Có chương trình đào tạo chính thức</span>
                  </div>
                )}
                {item.target_market && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
                    <Target size={15} className="text-orange-500 shrink-0" />
                    <span>Thị trường: <strong>{item.target_market}</strong></span>
                  </div>
                )}
                {item.total_locations != null && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
                    <Users size={15} className="text-orange-500 shrink-0" />
                    <span>Tổng chi nhánh: <strong>{formatNumber(item.total_locations)} cơ sở</strong></span>
                  </div>
                )}
                {item.established_year != null && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
                    <Calendar size={15} className="text-orange-500 shrink-0" />
                    <span>Năm thành lập: <strong>{item.established_year}</strong></span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Right: Sidebar ── */}
        <div className="space-y-4">
          {/* Company Card */}
          {item.company && (
            <Card>
              <CardHeader>
                <CardTitle>Thông tin công ty</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl border border-gray-100 flex items-center justify-center shrink-0 p-1 bg-white shadow-sm">
                    {item.company.logoUrl ? (
                      <img src={item.company.logoUrl} alt={item.company.name} className="w-full h-full object-contain" />
                    ) : (
                      <Building2 size={28} className="text-gray-300" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 leading-tight">
                      {item.company.name}
                    </h3>
                    {item.company.businessSectorName && (
                      <p className="text-xs text-gray-500 mt-0.5">{item.company.businessSectorName}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-xs border-t pt-3" style={{ borderColor: "var(--border-c)" }}>
                  {item.company.size && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Quy mô:</span>
                      <span className="font-medium text-gray-800">{item.company.size} nhân viên</span>
                    </div>
                  )}
                  {item.company.address && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Địa chỉ:</span>
                      <span className="font-medium text-gray-800 text-right truncate max-w-[160px]">{item.company.address}</span>
                    </div>
                  )}
                </div>

                <Link
                  to={`/cong-ty/${item.company.id}`}
                  className="text-orange-600 text-xs font-bold flex items-center justify-center gap-1 hover:underline pt-2 border-t text-center w-full"
                  style={{ borderColor: "var(--border-c)" }}
                >
                  Xem trang công ty <ExternalLink size={13} />
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Contact Card */}
          <Card>
            <CardHeader>
              <CardTitle>Thông tin liên hệ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-dim)" }}
                >
                  <UserIcon size={16} style={{ color: "var(--brand)" }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--text-3)" }}>
                    Người đăng
                  </p>
                  <p
                    className="font-semibold text-sm"
                    style={{ color: "var(--text-1)" }}
                  >
                    {item.creator?.username || "Chủ thương hiệu"}
                  </p>
                  <p className="text-xs" style={{ color: "var(--text-3)" }}>
                    {item.creator?.email || item.contact_email || ""}
                  </p>
                </div>
              </div>

              {can.sendOffer(user) && (
                <>
                  {needsToSignNda ? (
                    <button
                      onClick={() => setNdaOpen(true)}
                      className="w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all btn-brand"
                    >
                      Ký NDA để Gửi yêu cầu
                    </button>
                  ) : (
                    <button
                      onClick={() => setOfferOpen(true)}
                      disabled={isOffered}
                      className={cn(
                        "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all",
                        isOffered
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "btn-brand"
                      )}
                    >
                      {isOffered ? "Đã gửi yêu cầu" : "Gửi yêu cầu nhượng quyền"}
                    </button>
                  )}
                </>
              )}

              {(!user || user.id !== ownerId) && (
                <button
                  onClick={() => toggleSave(item.id, "Franchise")}
                  className={cn(
                    "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 border transition-all mt-2",
                    saved
                      ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <Heart size={14} className={cn(saved && "fill-orange-500 text-orange-500")} />
                  {saved ? "Đã lưu tin" : "Lưu tin"}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Meta Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Chi tiết</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {[
                  ["Loại hình", item.businessTypeName || (item as any).businessType],
                  ["Ngành", item.businessSectorName || (item as any).businessSector],
                  ["Tỉnh/TP", item.province?.name || item.province],
                  ["Quận/Huyện", item.district?.name || item.district],
                  ["Phường/Xã", item.ward?.name || item.ward],
                  ["Quốc gia", item.country?.name || item.country],
                  [
                    "Trạng thái",
                    item.approvalStatus === "approved"
                      ? "✓ Đã duyệt"
                      : "⏳ Chờ duyệt",
                  ],
                  [
                    "Ngày đăng",
                    item.createdAt ? new Date(item.createdAt).toLocaleDateString("vi-VN") : null,
                  ],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between items-start py-2 border-b last:border-0 gap-2"
                    style={{ borderColor: "var(--border-c)" }}
                  >
                    <span
                      className="text-xs shrink-0"
                      style={{ color: "var(--text-3)" }}
                    >
                      {k}
                    </span>
                    <span
                      className="text-xs font-medium text-right"
                      style={{ color: "var(--text-1)" }}
                    >
                      {v ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Financial Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle>Hiệu quả kinh doanh</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                {
                  label: "Vốn tối thiểu",
                  value: formatPrice(item.investment_required_min, item.currencyUnit),
                  color: "var(--brand)",
                },
                {
                  label: "Vốn tối đa",
                  value: formatPrice(item.investment_required_max, item.currencyUnit),
                  color: "var(--text-1)",
                },
                {
                  label: "Biên lợi nhuận",
                  value: item.profit_margin != null ? `~${item.profit_margin}%` : "Liên hệ",
                  color: "#16a34a",
                },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-xs" style={{ color: "var(--text-3)" }}>
                    {label}
                  </span>
                  <span className="text-sm font-bold" style={{ color }}>
                    {value}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ══ OFFER MODAL ══ */}
      <OfferModal
        isOpen={offerOpen}
        onClose={() => setOfferOpen(false)}
        title="Gửi yêu cầu nhượng quyền"
        onSubmit={handleSubmit(onSubmit)}
        isPending={isPending}
      >
        <Textarea
          label="Lời nhắn *"
          placeholder="Giới thiệu bản thân và lý do muốn hợp tác nhượng quyền..."
          rows={4}
          error={errors.message?.message}
          {...register("message")}
        />
      </OfferModal>

      {/* ══ NDA MODAL ══ */}
      <NdaSignModal
        isOpen={ndaOpen}
        onClose={() => setNdaOpen(false)}
        entityType="Franchise"
        entityId={item.id}
        description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để trao đổi với đối tác."
        onSuccess={() => {
          setNdaOpen(false);
          setOfferOpen(true);
        }}
      />
    </div>
  );
}
